import { useEffect, useMemo, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
// サイドバー・ツールバー（実画面 SandboxScreen 準拠の装飾）用アイコン
import LibraryAddTwoToneIcon from "@mui/icons-material/LibraryAddTwoTone";
import DriveFileRenameOutlineTwoToneIcon from "@mui/icons-material/DriveFileRenameOutlineTwoTone";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlaylistAddCheckCircleTwoToneIcon from "@mui/icons-material/PlaylistAddCheckCircleTwoTone";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import RefreshIcon from "@mui/icons-material/Refresh";
import ListAltIcon from "@mui/icons-material/ListAlt";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import KebabDiningOutlinedIcon from "@mui/icons-material/KebabDiningOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import type { TutorialAction } from "./types.ts";
import {
  PaletteColor,
  PaletteColorHex,
} from "../../types/PaletteColor.ts";

/**
 * 「ダッシュボードのカスタマイズ」チュートリアル専用の練習用ダッシュボード画面。
 *
 * 実画面（DashboardView）を簡略再現した静的画面。DB・API は一切呼ばず、
 * すべて内部 state で完結する。ステップを進める操作が行われたら onAction で通知する。
 * data-tut 属性は dashboardTutorialSteps.ts の targetKey と対応する。
 */

interface Props {
  accent: string;
  isDark: boolean;
  onAction: (action: TutorialAction) => void;
  /**
   * スキップ（操作の代行）用。ダッシュボードの状態はこのコンポーネントが持つため、
   * 期待される操作を代行する関数をこの ref に登録し、親（TutorialSandbox）から呼び出す。
   */
  simulateRef?: React.MutableRefObject<
    ((action: TutorialAction) => void) | null
  >;
}

type ScoreMode = "deadline" | "status" | "priority" | "tags" | "assignee";
type Mode = "common" | "personal";

interface SampleTag {
  name: string;
  color: string;
}
interface SampleTask {
  id: string;
  title: string;
  status: string;
  assignee: string;
  /** 期限までの残り日数。null は期限なし、負数は超過 */
  daysUntil: number | null;
  priority: "高" | "中" | "低" | "なし";
  tags: SampleTag[];
  /** カスタム項目の値（列キー -> 値）。練習用ダミー */
  custom?: Record<string, string>;
}

/** 練習用のカスタム項目列（全体設定のカスタム項目で追加済みという想定） */
const SAMPLE_CUSTOM_COLUMN = { key: "custom_1", label: "見積工数" };

interface DeadlineCard {
  /** React の key・編集対象の識別に使う安定した ID（範囲は編集で変わるため使わない） */
  id: number;
  /** 期限までの残日数の範囲（null=制限なし。片方のみの指定を許可） */
  minDays: number | null;
  maxDays: number | null;
  label: string;
  /** カードの文字色（PaletteColor 名） */
  color: PaletteColor;
}

// カラーピッカーの選択肢（PaletteColor 全色）
const PALETTE_COLOR_KEYS = Object.keys(PaletteColorHex) as PaletteColor[];

// 期限カードの安定 ID を採番するカウンタ（範囲と独立させ、key 衝突を防ぐ）
let deadlineCardIdSeq = 0;
const nextCardId = (): number => ++deadlineCardIdSeq;
interface Scorecard {
  /** 期限・ステータス以外に追加した集計カテゴリ */
  modes: ScoreMode[];
  deadlineCards: DeadlineCard[];
}

interface ColumnItem {
  key: string;
  label: string;
  visible: boolean;
  /** カスタム項目の列なら true（実画面同様「カスタム」バッジを表示） */
  isCustom?: boolean;
}

const STATUS_COLOR: Record<string, string> = {
  未対応: "#8A94A6",
  対応中: "#3989FA",
  待機: "#F59E0B",
  完了: "#40C063",
};

const PRIORITY_COLOR: Record<string, string> = {
  高: "#EF4444",
  中: "#F97316",
  低: "#3B82F6",
  なし: "#9E9E9E",
};

const SAMPLE_TASKS: SampleTask[] = [
  {
    id: "t1",
    title: "見積書を作成する",
    status: "未対応",
    assignee: "あなた",
    daysUntil: -2,
    priority: "高",
    tags: [{ name: "重要", color: "#F85B52" }],
    custom: { custom_1: "3h" },
  },
  {
    id: "t2",
    title: "定例会議の議事録をまとめる",
    status: "対応中",
    assignee: "佐藤 太郎",
    daysUntil: 0,
    priority: "中",
    tags: [{ name: "会議", color: "#3989FA" }],
    custom: { custom_1: "1h" },
  },
  {
    id: "t3",
    title: "提案資料をレビューする",
    status: "対応中",
    assignee: "鈴木 花子",
    daysUntil: 3,
    priority: "低",
    tags: [{ name: "資料作成", color: "#40C063" }],
    custom: { custom_1: "5h" },
  },
  {
    id: "t4",
    title: "請求処理を確認する",
    status: "完了",
    assignee: "あなた",
    daysUntil: null,
    priority: "なし",
    tags: [],
    custom: { custom_1: "2h" },
  },
  {
    id: "t5",
    title: "来月のスケジュールを立てる",
    status: "未対応",
    assignee: "高橋 健",
    daysUntil: 8,
    priority: "中",
    tags: [{ name: "会議", color: "#3989FA" }],
    custom: { custom_1: "4h" },
  },
];

const OPTIONAL_MODES: { mode: ScoreMode; label: string }[] = [
  { mode: "priority", label: "重要度" },
  { mode: "tags", label: "タグ" },
  { mode: "assignee", label: "担当者" },
];

const MODE_LABEL: Record<ScoreMode, string> = {
  deadline: "期限",
  status: "ステータス",
  priority: "重要度",
  tags: "タグ",
  assignee: "担当者",
};

const DEFAULT_COLUMNS: ColumnItem[] = [
  { key: "status", label: "ステータス", visible: true },
  { key: "assignee", label: "担当者", visible: true },
  { key: "endDate", label: "期限", visible: true },
  { key: "priority", label: "重要度", visible: true },
  { key: "tags", label: "タグ", visible: true },
  // カスタム項目を全体設定で追加すると、列としてもここに現れる
  {
    key: SAMPLE_CUSTOM_COLUMN.key,
    label: SAMPLE_CUSTOM_COLUMN.label,
    visible: true,
    isCustom: true,
  },
];

const cloneColumns = (): ColumnItem[] =>
  DEFAULT_COLUMNS.map((c) => ({ ...c }));

const defaultScorecard = (): Scorecard => ({
  modes: [],
  deadlineCards: [
    { id: nextCardId(), minDays: null, maxDays: 0, label: "今日", color: "Orange" },
    {
      id: nextCardId(),
      minDays: null,
      maxDays: 7,
      label: "1週間以内",
      color: "Orange",
    },
    {
      id: nextCardId(),
      minDays: null,
      maxDays: 30,
      label: "1ヶ月以内",
      color: "Orange",
    },
    {
      id: nextCardId(),
      minDays: null,
      maxDays: 60,
      label: "2ヶ月以内",
      color: "Orange",
    },
  ],
});

/** 期限バケットのキーをすべて返す（overdue / dl_<id>）。期間が重複するカードには重複してカウント。期限なし・該当カードなしの場合は空配列 */
function deadlineKeys(task: SampleTask, cards: DeadlineCard[]): string[] {
  if (task.daysUntil === null) return [];
  if (task.daysUntil < 0 && task.status !== "完了") return ["overdue"];
  const keys: string[] = [];
  for (const c of cards) {
    if (c.minDays == null && c.maxDays == null) continue;
    if (c.minDays != null && task.daysUntil < c.minDays) continue;
    if (c.maxDays != null && task.daysUntil > c.maxDays) continue;
    keys.push(`dl_${c.id}`);
  }
  return keys;
}

function endDateLabel(d: number | null): string {
  if (d === null) return "—";
  if (d === 0) return "今日";
  if (d < 0) return `${-d}日超過`;
  return `${d}日後`;
}

// ===== 左サイドバー（装飾・実画面 SandboxScreen 準拠。操作しても何も起きない） =====

const SAMPLE_PROJECTS = ["営業チーム", "開発プロジェクト", "総務タスク"];

const DashboardSandboxSidebar = ({
  accent,
  isDark,
}: {
  accent: string;
  isDark: boolean;
}) => {
  const surface = isDark ? "#1e1e1e" : "#ffffff";
  const text = isDark ? "#e0e0e0" : "#212121";
  const subText = isDark ? "#9e9e9e" : "#757575";
  const border = isDark ? "#333333" : "#e0e0e0";
  const inputBg = isDark ? "#2a2a2a" : "#ffffff";

  const activeStyle: React.CSSProperties = {
    backgroundColor: accent,
    backgroundImage:
      "linear-gradient(to bottom, rgba(255,255,255,0.15), rgba(255,255,255,0))",
    boxShadow:
      "inset 0 1px 2px rgba(255,255,255,0.25), 0 3px 5px rgba(0,0,0,0.15)",
  };

  const sectionTitle = (label: string) => (
    <span
      style={{
        fontSize: "0.8rem",
        fontWeight: 700,
        color: subText,
        display: "block",
        marginBottom: 8,
      }}
    >
      {label}
    </span>
  );

  const filterToggle = (label: string) => (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        fontSize: "0.85rem",
        paddingLeft: 8,
      }}
    >
      <span style={{ color: text }}>{label}</span>
      <span
        style={{
          width: 44,
          height: 24,
          borderRadius: 9999,
          backgroundColor: "#d1d5db",
          position: "relative",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 2,
            left: 2,
            width: 20,
            height: 20,
            borderRadius: 9999,
            backgroundColor: "#fff",
            boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
          }}
        />
      </span>
    </label>
  );

  return (
    <aside
      style={{
        width: 320,
        flexShrink: 0,
        backgroundColor: surface,
        borderRight: `2px solid ${border}`,
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {/* プロジェクトセクション */}
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 6,
            }}
          >
            <span
              style={{ fontSize: "0.8rem", fontWeight: 700, color: subText }}
            >
              プロジェクト
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
              <span style={{ display: "flex", color: subText, padding: 6 }}>
                <LibraryAddTwoToneIcon style={{ fontSize: 18 }} />
              </span>
              <span style={{ display: "flex", color: subText, padding: 6 }}>
                <DriveFileRenameOutlineTwoToneIcon style={{ fontSize: 18 }} />
              </span>
              <span style={{ display: "flex", color: subText, padding: 6 }}>
                <ExpandMoreIcon style={{ fontSize: 18 }} />
              </span>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {SAMPLE_PROJECTS.map((name, i) => {
              const isActive = i === 0;
              return (
                <div
                  key={name}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "5px 8px",
                    borderRadius: 8,
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    color: isActive ? "#fff" : text,
                    ...(isActive ? activeStyle : {}),
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    <PlaylistAddCheckCircleTwoToneIcon
                      style={{
                        fontSize: 18,
                        marginRight: 8,
                        flexShrink: 0,
                        color: isActive ? "#fff" : subText,
                      }}
                    />
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {name}
                    </span>
                  </div>
                  <MoreVertIcon
                    style={{
                      fontSize: 16,
                      flexShrink: 0,
                      color: isActive ? "#fff" : subText,
                      opacity: 0.45,
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* ソートセクション */}
        <div style={{ borderTop: `1px solid ${border}`, paddingTop: 12 }}>
          {sectionTitle("ソート")}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div
              style={{
                flex: 1,
                padding: "6px 8px",
                fontSize: "0.8rem",
                border: `1px solid ${border}`,
                borderRadius: 6,
                backgroundColor: inputBg,
                color: text,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              作成日順
              <ExpandMoreIcon style={{ fontSize: 16, color: subText }} />
            </div>
            <span
              style={{
                display: "flex",
                padding: 6,
                border: `1px solid ${border}`,
                borderRadius: 6,
                color: subText,
              }}
            >
              <SwapVertIcon style={{ fontSize: 16 }} />
            </span>
          </div>
        </div>

        {/* フィルターセクション */}
        <div style={{ borderTop: `1px solid ${border}`, paddingTop: 12 }}>
          {sectionTitle("フィルター")}
          <div style={{ position: "relative", marginBottom: 10 }}>
            <SearchIcon
              style={{
                position: "absolute",
                left: 8,
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: 16,
                color: subText,
              }}
            />
            <input
              type="text"
              readOnly
              placeholder="メインタスク名で絞り込み"
              style={{
                width: "100%",
                padding: "6px 8px 6px 30px",
                fontSize: "0.8rem",
                border: `1px solid ${border}`,
                borderRadius: 6,
                outline: "none",
                backgroundColor: inputBg,
                color: text,
              }}
            />
          </div>
          <div
            style={{
              borderTop: `1px dashed ${border}`,
              paddingTop: 8,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            {filterToggle("自分が担当者")}
            {filterToggle("自分が依頼者")}
            {filterToggle("期限が今日を含むタスク")}
            {filterToggle("完了タスクを表示")}
          </div>
        </div>

        {/* その他セクション */}
        <div style={{ borderTop: `1px solid ${border}`, paddingTop: 12 }}>
          {sectionTitle("その他")}
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: 8,
                borderRadius: 8,
                fontSize: "0.85rem",
                color: text,
                gap: 8,
              }}
            >
              <AccessAlarmIcon style={{ fontSize: 18, color: subText }} />
              リマインダー管理
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: 8,
                borderRadius: 8,
                fontSize: "0.85rem",
                color: text,
                gap: 8,
              }}
            >
              <AutorenewIcon style={{ fontSize: 18, color: subText }} />
              繰り返し作成一覧
            </div>
          </div>
        </div>
      </div>

      {/* 下部ボタン（装飾） */}
      <div style={{ padding: 16, borderTop: `1px solid ${border}` }}>
        <div
          style={{
            width: "100%",
            padding: "8px 16px",
            borderRadius: 8,
            fontSize: "0.85rem",
            fontWeight: 600,
            color: "#fff",
            textAlign: "center",
            border: "2px solid #fff",
            ...activeStyle,
          }}
        >
          詳細な絞り込み
        </div>
        <div
          style={{
            width: "100%",
            marginTop: 8,
            fontSize: "0.8rem",
            color: accent,
            textAlign: "center",
          }}
        >
          絞り込みをリセット
        </div>
      </div>
    </aside>
  );
};

// ===== 上部ツールバー（装飾・ビュー切替。ダッシュボードがアクティブ） =====

const DashboardSandboxToolbar = ({
  accent,
  isDark,
}: {
  accent: string;
  isDark: boolean;
}) => {
  const surface = isDark ? "#1e1e1e" : "#ffffff";
  const subText = isDark ? "#9e9e9e" : "#757575";
  const border = isDark ? "#333333" : "#e0e0e0";

  const activeStyle: React.CSSProperties = {
    backgroundColor: accent,
    backgroundImage:
      "linear-gradient(to bottom, rgba(255,255,255,0.15), rgba(255,255,255,0))",
    boxShadow:
      "inset 0 1px 2px rgba(255,255,255,0.25), 0 3px 5px rgba(0,0,0,0.15)",
  };

  const iconBtn = (
    icon: React.ReactNode,
    active = false,
  ): React.ReactNode => (
    <span
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 6,
        margin: "0 1px",
        borderRadius: 6,
        color: active ? "#fff" : subText,
        ...(active ? activeStyle : {}),
      }}
    >
      {icon}
    </span>
  );

  const divider = (
    <span
      style={{
        width: 1,
        alignSelf: "stretch",
        margin: "0 4px",
        backgroundColor: border,
      }}
    />
  );

  return (
    <div style={{ padding: "12px 24px 0" }}>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 2,
          padding: "2px 4px",
          backgroundColor: surface,
          border: `1px solid ${border}`,
          borderRadius: 8,
          boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
        }}
      >
        {iconBtn(<RefreshIcon style={{ fontSize: 18 }} />)}
        {divider}
        {/* ビュー切替（ダッシュボードがアクティブ） */}
        {iconBtn(<ListAltIcon style={{ fontSize: 18 }} />)}
        {iconBtn(<DashboardOutlinedIcon style={{ fontSize: 18 }} />, true)}
        {iconBtn(<TableChartOutlinedIcon style={{ fontSize: 18 }} />)}
        {iconBtn(<AccountTreeOutlinedIcon style={{ fontSize: 18 }} />)}
        {iconBtn(<CalendarMonthIcon style={{ fontSize: 18 }} />)}
        {iconBtn(<KebabDiningOutlinedIcon style={{ fontSize: 18 }} />)}
        {divider}
        {iconBtn(<SettingsIcon style={{ fontSize: 20 }} />)}
      </div>
    </div>
  );
};


export const DashboardSandboxScreen = ({
  accent,
  isDark,
  onAction,
  simulateRef,
}: Props) => {
  const bg = isDark ? "#121212" : "#f5f5f5";
  const cardBg = isDark ? "#1e1e1e" : "#ffffff";
  const border = isDark ? "#333" : "#e5e7eb";
  const textPrimary = isDark ? "#e0e0e0" : "#212121";
  const textSecondary = isDark ? "#9e9e9e" : "#757575";
  const inputBg = isDark ? "#121212" : "#ffffff";

  // ===== スコアカード state（共通・個人で独立） =====
  const [scorecardMode, setScorecardMode] = useState<Mode>("common");
  const [commonSc, setCommonSc] = useState<Scorecard>(defaultScorecard);
  const [personalSc, setPersonalSc] = useState<Scorecard>(defaultScorecard);
  const activeSc = scorecardMode === "common" ? commonSc : personalSc;

  const [scoreMode, setScoreMode] = useState<ScoreMode>("deadline");
  const [selectedCard, setSelectedCard] = useState<string | null>(null);

  const [isEditMode, setIsEditMode] = useState(false);
  const [draft, setDraft] = useState<Scorecard | null>(null);
  const [editingCardIdx, setEditingCardIdx] = useState<number | null>(null);
  const [isCardColorPickerOpen, setIsCardColorPickerOpen] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);

  // 編集中は draft を、それ以外は保存済みを参照
  const effectiveSc = isEditMode && draft ? draft : activeSc;
  const visibleModes: ScoreMode[] = ["deadline", "status", ...effectiveSc.modes];

  const addableModes = OPTIONAL_MODES.filter(
    (m) => !effectiveSc.modes.includes(m.mode),
  );

  // ===== 列表示設定 state（共通・個人で独立） =====
  const [columnMode, setColumnMode] = useState<Mode>("common");
  const [savedCommonCols, setSavedCommonCols] =
    useState<ColumnItem[]>(cloneColumns);
  const [savedPersonalCols, setSavedPersonalCols] =
    useState<ColumnItem[]>(cloneColumns);
  const [showColumnDialog, setShowColumnDialog] = useState(false);

  const activeColumns =
    columnMode === "common" ? savedCommonCols : savedPersonalCols;
  const visibleColumnKeys = activeColumns
    .filter((c) => c.visible)
    .map((c) => c.key);

  // ===== 集計 =====
  const cards = useMemo(() => {
    if (scoreMode === "deadline") {
      const counts: Record<string, number> = { overdue: 0 };
      for (const c of effectiveSc.deadlineCards)
        counts[`dl_${c.id}`] = 0;
      for (const t of SAMPLE_TASKS) {
        for (const k of deadlineKeys(t, effectiveSc.deadlineCards)) {
          counts[k] = (counts[k] ?? 0) + 1;
        }
      }
      return counts;
    }
    if (scoreMode === "status") {
      const c: Record<string, number> = {};
      for (const t of SAMPLE_TASKS) c[t.status] = (c[t.status] ?? 0) + 1;
      return c;
    }
    if (scoreMode === "priority") {
      const c: Record<string, number> = { 高: 0, 中: 0, 低: 0, なし: 0 };
      for (const t of SAMPLE_TASKS) c[t.priority] = (c[t.priority] ?? 0) + 1;
      return c;
    }
    if (scoreMode === "tags") {
      const c: Record<string, number> = {};
      for (const t of SAMPLE_TASKS) {
        if (t.tags.length === 0) c["タグなし"] = (c["タグなし"] ?? 0) + 1;
        else for (const tag of t.tags) c[tag.name] = (c[tag.name] ?? 0) + 1;
      }
      return c;
    }
    // assignee
    const c: Record<string, number> = {};
    for (const t of SAMPLE_TASKS) c[t.assignee] = (c[t.assignee] ?? 0) + 1;
    return c;
  }, [scoreMode, effectiveSc]);

  // ===== ハンドラ =====
  const enterEdit = () => {
    setDraft(JSON.parse(JSON.stringify(activeSc)));
    setIsEditMode(true);
    setEditingCardIdx(null);
    setShowAddMenu(false);
    onAction("dash-scorecard-edit");
  };

  const saveEdit = () => {
    if (draft) {
      if (scorecardMode === "common") setCommonSc(draft);
      else setPersonalSc(draft);
    }
    setIsEditMode(false);
    setDraft(null);
    setEditingCardIdx(null);
    setShowAddMenu(false);
    setSelectedCard(null);
    onAction("dash-scorecard-save");
  };

  const changeScorecardMode = (mode: Mode) => {
    if (mode === scorecardMode) return;
    setScorecardMode(mode);
    setScoreMode("deadline");
    setSelectedCard(null);
    // 保存先を切り替えたら編集中の draft は作り直す
    if (isEditMode) {
      const target = mode === "common" ? commonSc : personalSc;
      setDraft(JSON.parse(JSON.stringify(target)));
      setEditingCardIdx(null);
      setShowAddMenu(false);
    }
    if (mode === "personal") onAction("dash-scorecard-personal");
  };

  /** 「カテゴリを追加」ボタン: 追加できるカテゴリのメニューを開く。 */
  const openAddMenu = () => {
    if (addableModes.length === 0) return;
    setShowAddMenu((v) => !v);
    onAction("dash-scorecard-open-add-menu");
  };

  /** メニューから追加するカテゴリを選ぶ。 */
  const pickCategory = (mode: ScoreMode) => {
    setDraft((prev) =>
      prev && !prev.modes.includes(mode)
        ? { ...prev, modes: [...prev.modes, mode] }
        : prev,
    );
    setShowAddMenu(false);
    onAction("dash-scorecard-add-category");
  };

  const removeCategory = (mode: ScoreMode) => {
    setDraft((prev) =>
      prev ? { ...prev, modes: prev.modes.filter((m) => m !== mode) } : prev,
    );
    if (scoreMode === mode) setScoreMode("deadline");
  };

  const addDeadlineCard = () => {
    setDraft((prev) => {
      if (!prev) return prev;
      const maxUsed = prev.deadlineCards.length
        ? Math.max(...prev.deadlineCards.map((c) => c.maxDays ?? c.minDays ?? -1))
        : -1;
      const t = maxUsed + 1;
      return {
        ...prev,
        deadlineCards: [
          ...prev.deadlineCards,
          {
            id: nextCardId(),
            minDays: null,
            maxDays: t,
            label: `${t}日以内`,
            color: "Orange",
          },
        ],
      };
    });
    onAction("dash-scorecard-add-deadline-card");
  };

  /** 期限カードの鉛筆アイコンで編集フォームを開く。 */
  const openCardEdit = (idx: number) => {
    setEditingCardIdx(idx);
    setIsCardColorPickerOpen(false);
    onAction("dash-scorecard-edit-deadline-card");
  };

  /** 期限カードの編集フォームをチェックで確定する。 */
  const confirmCardEdit = () => {
    setEditingCardIdx(null);
    setIsCardColorPickerOpen(false);
    onAction("dash-scorecard-confirm-deadline-card");
  };

  const updateDeadlineCard = (
    idx: number,
    patch: Partial<DeadlineCard>,
  ) => {
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            deadlineCards: prev.deadlineCards.map((c, i) =>
              i === idx ? { ...c, ...patch } : c,
            ),
          }
        : prev,
    );
  };

  const removeDeadlineCard = (idx: number) => {
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            deadlineCards: prev.deadlineCards.filter((_, i) => i !== idx),
          }
        : prev,
    );
    setEditingCardIdx(null);
  };

  const handleCardClick = (key: string) => {
    if (isEditMode) return;
    setSelectedCard((prev) => (prev === key ? null : key));
  };

  // ===== スキップ（操作の代行） =====
  // 列表示設定ダイアログの状態（タブ・編集中の列）はダイアログ側が持つため、
  // ダイアログにも代行関数を登録してもらい、ここから委譲する。
  const columnDialogSimulateRef = useRef<
    ((action: TutorialAction) => void) | null
  >(null);

  /**
   * 操作待ちステップの「スキップ」用に、ユーザーが取るべき操作を代行する。
   * 通常操作と同じハンドラを呼ぶことで onAction（＝ステップ進行）も 1 回だけ発火する。
   */
  const simulateAction = (action: TutorialAction) => {
    switch (action) {
      case "dash-scorecard-edit":
        enterEdit();
        break;
      case "dash-scorecard-add-deadline-card":
        addDeadlineCard();
        break;
      case "dash-scorecard-edit-deadline-card":
        // 直前に追加した（＝末尾の）期限カードの編集フォームを開く
        openCardEdit(Math.max(0, effectiveSc.deadlineCards.length - 1));
        break;
      case "dash-scorecard-confirm-deadline-card":
        confirmCardEdit();
        break;
      case "dash-scorecard-open-add-menu":
        setShowAddMenu(true);
        onAction(action);
        break;
      case "dash-scorecard-add-category": {
        const mode = addableModes[0]?.mode;
        if (mode) {
          pickCategory(mode);
        } else {
          // すべて追加済みなら追加操作は不要なので進行のみ行う
          setShowAddMenu(false);
          onAction(action);
        }
        break;
      }
      case "dash-scorecard-save":
        saveEdit();
        break;
      case "dash-scorecard-personal":
        if (scorecardMode === "personal") onAction(action);
        else changeScorecardMode("personal");
        break;
      case "dash-open-column-settings":
        setShowColumnDialog(true);
        onAction(action);
        break;
      case "dash-column-personal":
      case "dash-column-save":
        if (columnDialogSimulateRef.current)
          columnDialogSimulateRef.current(action);
        else onAction(action);
        break;
      default:
        // 想定外の操作は状態を変えず進行のみ行う
        onAction(action);
        break;
    }
  };

  // 最新の state を閉じ込めるため、毎レンダーで登録し直す
  useEffect(() => {
    if (!simulateRef) return;
    simulateRef.current = simulateAction;
    return () => {
      simulateRef.current = null;
    };
  });

  // ===== スコアカードのレンダリング（期限以外） =====
  const genericCards = (): { key: string; label: string; color: string }[] => {
    if (scoreMode === "status") {
      return ["未対応", "対応中", "完了"].map((s) => ({
        key: s,
        label: s,
        color: STATUS_COLOR[s] ?? accent,
      }));
    }
    if (scoreMode === "priority") {
      return ["高", "中", "低", "なし"].map((p) => ({
        key: p,
        label: p,
        color: PRIORITY_COLOR[p],
      }));
    }
    if (scoreMode === "tags") {
      const seen = new Map<string, string>();
      for (const t of SAMPLE_TASKS)
        for (const tag of t.tags) seen.set(tag.name, tag.color);
      const arr = [...seen.entries()].map(([label, color]) => ({
        key: label,
        label,
        color,
      }));
      if (cards["タグなし"])
        arr.push({ key: "タグなし", label: "タグなし", color: "#9E9E9E" });
      return arr;
    }
    // assignee
    const names = Array.from(new Set(SAMPLE_TASKS.map((t) => t.assignee)));
    return names.map((n) => ({ key: n, label: n, color: accent }));
  };

  const ScoreCard = ({
    label,
    color,
    count,
    keyName,
  }: {
    label: string;
    color: string;
    count: number;
    keyName: string;
  }) => {
    const selected = selectedCard === keyName;
    return (
      <button
        onClick={() => handleCardClick(keyName)}
        className="flex-shrink-0 rounded-xl px-4 py-3 text-center transition-all min-w-[100px]"
        style={{
          background: selected ? (isDark ? color + "33" : color + "18") : cardBg,
          border: `2px solid ${selected ? color : border}`,
          boxShadow: selected ? `0 0 0 1px ${color}40` : undefined,
        }}
      >
        <div className="text-xs font-medium mb-1" style={{ color }}>
          {label}
        </div>
        <div
          className="text-2xl font-bold"
          style={{ color: selected ? color : textPrimary }}
        >
          {count}
        </div>
        <div className="text-xs mt-0.5" style={{ color: textSecondary }}>
          タスク
        </div>
      </button>
    );
  };

  return (
    <div
      className="flex h-full overflow-hidden"
      style={{ background: bg, color: textPrimary }}
    >
      {/* ===== 左サイドバー（装飾・実画面準拠） ===== */}
      <DashboardSandboxSidebar accent={accent} isDark={isDark} />

      {/* ===== 右：ツールバー + ダッシュボード本体 ===== */}
      <div className="flex flex-col flex-1 min-w-0 relative">
        {/* 浮遊ツールバー（ビュー切替。ダッシュボードがアクティブ） */}
        <DashboardSandboxToolbar accent={accent} isDark={isDark} />

        <div className="flex flex-col flex-1 overflow-hidden">
      {/* ========== スコアエリア ========== */}
      <div
        className="flex-shrink-0 px-4 pt-4 pb-3"
        style={{ borderBottom: `1px solid ${border}` }}
      >
        {/* 行1: タイトル */}
        <div className="flex items-center gap-2 mb-2">
          <DashboardOutlinedIcon style={{ fontSize: 18, color: accent }} />
          <span className="text-sm font-semibold" style={{ color: accent }}>
            ダッシュボード
          </span>
        </div>

        {/* 行2: スコアモード タブ + 編集ボタン + 共通/個人 */}
        <div className="relative flex items-center mb-3 gap-1">
          <div className="flex-1 overflow-x-auto" data-tut="dash-score-tabs">
            <div
              className="flex rounded-lg overflow-hidden border text-xs"
              style={{ borderColor: border, width: "max-content" }}
            >
              {visibleModes.map((m) => {
                const removable =
                  isEditMode && m !== "deadline" && m !== "status";
                const isActive = scoreMode === m;
                return (
                  <div
                    key={m}
                    className="flex items-center whitespace-nowrap"
                    style={{ borderRight: `1px solid ${border}` }}
                  >
                    <button
                      onClick={() => {
                        setScoreMode(m);
                        setSelectedCard(null);
                      }}
                      className="px-3 py-1 transition-colors"
                      style={
                        isActive
                          ? { background: accent, color: "#fff" }
                          : { background: cardBg, color: textSecondary }
                      }
                    >
                      {MODE_LABEL[m]}
                    </button>
                    {removable && (
                      <button
                        onClick={() => removeCategory(m)}
                        title="このカテゴリを削除"
                        className="flex items-center px-1.5 py-1 hover:opacity-70"
                        style={{
                          background: isActive ? accent : cardBg,
                          color: isActive ? "#fff" : "#EF4444",
                        }}
                      >
                        <DeleteOutlineIcon style={{ fontSize: 14 }} />
                      </button>
                    )}
                  </div>
                );
              })}
              {/* カテゴリを追加（編集モード） */}
              {isEditMode && (
                <button
                  data-tut="dash-add-category"
                  onClick={openAddMenu}
                  disabled={addableModes.length === 0}
                  className="flex items-center gap-0.5 px-3 py-1 whitespace-nowrap transition-colors"
                  style={{
                    background: cardBg,
                    color: addableModes.length === 0 ? textSecondary : accent,
                    opacity: addableModes.length === 0 ? 0.5 : 1,
                  }}
                >
                  <AddIcon style={{ fontSize: 13 }} />
                  カテゴリを追加
                </button>
              )}
            </div>
          </div>

          {/* 表示を編集 / 修正を保存 */}
          <button
            data-tut="dash-edit-btn"
            onClick={isEditMode ? saveEdit : enterEdit}
            className="flex-shrink-0 flex items-center gap-1 px-3 py-1 rounded text-xs font-medium transition-colors hover:opacity-85"
            style={
              isEditMode
                ? { background: accent, color: "#fff" }
                : { border: `1px solid ${border}`, color: textSecondary }
            }
          >
            {isEditMode ? (
              <>
                <CheckIcon style={{ fontSize: 14 }} />
                修正を保存
              </>
            ) : (
              <>
                <EditOutlinedIcon style={{ fontSize: 14 }} />
                表示を編集
              </>
            )}
          </button>

          {/* 共通/個人 切り替え */}
          <div
            data-tut="dash-scorecard-mode"
            className="flex-shrink-0 flex rounded overflow-hidden text-xs"
            style={{ border: `1px solid ${border}` }}
          >
            <button
              onClick={() => changeScorecardMode("common")}
              className="flex items-center gap-0.5 px-2 py-1 transition-colors"
              style={
                scorecardMode === "common"
                  ? { color: accent, background: accent + "18" }
                  : { color: textSecondary }
              }
            >
              <PeopleOutlineIcon style={{ fontSize: 12 }} />
              共通
            </button>
            <button
              onClick={() => changeScorecardMode("personal")}
              className="flex items-center gap-0.5 px-2 py-1 transition-colors"
              style={
                scorecardMode === "personal"
                  ? { color: accent, background: accent + "18" }
                  : { color: textSecondary }
              }
            >
              <PersonOutlineIcon style={{ fontSize: 12 }} />
              個人
            </button>
          </div>

          {/* カテゴリ追加メニュー（実画面同様のドロップダウン） */}
          {isEditMode && showAddMenu && (
            <>
              <div
                className="fixed inset-0 z-20"
                onClick={() => setShowAddMenu(false)}
              />
              <div
                data-tut="dash-add-category-menu"
                className="absolute z-30 top-full left-0 mt-1 rounded-lg shadow-xl overflow-hidden"
                style={{
                  background: cardBg,
                  border: `1px solid ${border}`,
                  minWidth: 180,
                }}
              >
                <div
                  className="px-3 py-2 text-xs font-semibold"
                  style={{
                    color: textSecondary,
                    borderBottom: `1px solid ${border}`,
                  }}
                >
                  追加するカテゴリ
                </div>
                {addableModes.length === 0 ? (
                  <div
                    className="px-3 py-3 text-xs"
                    style={{ color: textSecondary }}
                  >
                    追加できるカテゴリはありません
                  </div>
                ) : (
                  addableModes.map((def) => (
                    <button
                      key={def.mode}
                      onClick={() => pickCategory(def.mode)}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left transition-colors hover:bg-black/5"
                      style={{ color: textPrimary }}
                    >
                      <AddIcon style={{ fontSize: 15, color: accent }} />
                      {def.label}
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        {/* スコアカード本体 */}
        <div
          data-tut="dash-scorecards"
          className={`flex gap-3 overflow-x-auto pb-1 items-stretch ${
            isEditMode && scoreMode === "deadline" ? "pt-3" : ""
          }`}
        >
          {scoreMode === "deadline" ? (
            <>
              <ScoreCard
                keyName="overdue"
                label="期限切れ"
                color="#EF4444"
                count={cards["overdue"] ?? 0}
              />
              {effectiveSc.deadlineCards.map((card, idx) => {
                const key = `dl_${card.id}`;
                const editing = isEditMode && editingCardIdx === idx;
                const cardHex = PaletteColorHex[card.color] ?? "#F97316";
                if (editing) {
                  return (
                    <div
                      key={card.id}
                      className="flex-shrink-0 rounded-xl px-3 py-2.5 min-w-[210px] flex flex-col gap-1.5"
                      style={{
                        background: cardBg,
                        border: `2px solid ${cardHex}`,
                      }}
                    >
                      <div className="flex items-center gap-1.5">
                        <div className="relative flex-shrink-0">
                          <button
                            onClick={() =>
                              setIsCardColorPickerOpen((prev) => !prev)
                            }
                            title="カードの色を変更"
                            aria-label="カードの色を変更"
                            className="block w-5 h-5 rounded-full shadow-md transition-transform hover:scale-110"
                            style={{ backgroundColor: cardHex }}
                          />
                          {isCardColorPickerOpen && (
                            <div
                              className="absolute left-0 top-full mt-2 p-2 rounded-md shadow-lg z-20 grid grid-cols-5 gap-2 w-40"
                              style={{
                                background: isDark ? "#2a2a2a" : "#fff",
                              }}
                            >
                              {PALETTE_COLOR_KEYS.map((colorOption) => (
                                <button
                                  key={colorOption}
                                  onClick={() => {
                                    updateDeadlineCard(idx, {
                                      color: colorOption,
                                    });
                                    setIsCardColorPickerOpen(false);
                                  }}
                                  className="w-6 h-6 rounded-full transition-transform hover:scale-110 shadow-md flex items-center justify-center"
                                  style={{
                                    backgroundColor:
                                      PaletteColorHex[colorOption],
                                  }}
                                  aria-label={colorOption}
                                >
                                  {card.color === colorOption && (
                                    <CheckIcon
                                      style={{
                                        color: "white",
                                        fontSize: "1rem",
                                      }}
                                    />
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <input
                          type="text"
                          value={card.label}
                          onChange={(e) =>
                            updateDeadlineCard(idx, { label: e.target.value })
                          }
                          placeholder="表示名"
                          className="flex-1 min-w-0 text-xs border rounded px-1.5 py-1 focus:outline-none"
                          style={{
                            borderColor: border,
                            background: inputBg,
                            color: textPrimary,
                          }}
                        />
                      </div>
                      <div className="flex items-center gap-1">
                        <span
                          className="text-xs"
                          style={{ color: textSecondary }}
                        >
                          期限
                        </span>
                        <input
                          type="number"
                          min={0}
                          max={365}
                          value={card.minDays ?? ""}
                          placeholder="0"
                          onChange={(e) =>
                            updateDeadlineCard(idx, {
                              minDays:
                                e.target.value === ""
                                  ? null
                                  : Math.max(0, parseInt(e.target.value, 10) || 0),
                            })
                          }
                          className="w-12 text-center rounded border px-1 py-1 text-xs focus:outline-none"
                          style={{
                            borderColor: cardHex,
                            background: inputBg,
                            color: textPrimary,
                          }}
                        />
                        <span
                          className="text-xs"
                          style={{ color: textSecondary }}
                        >
                          ～
                        </span>
                        <input
                          type="number"
                          min={0}
                          max={365}
                          value={card.maxDays ?? ""}
                          placeholder="∞"
                          onChange={(e) =>
                            updateDeadlineCard(idx, {
                              maxDays:
                                e.target.value === ""
                                  ? null
                                  : Math.max(0, parseInt(e.target.value, 10) || 0),
                            })
                          }
                          className="w-12 text-center rounded border px-1 py-1 text-xs focus:outline-none"
                          style={{
                            borderColor: cardHex,
                            background: inputBg,
                            color: textPrimary,
                          }}
                        />
                        <span
                          className="text-xs"
                          style={{ color: textSecondary }}
                        >
                          日
                        </span>
                        <button
                          onClick={confirmCardEdit}
                          disabled={
                            (card.minDays == null && card.maxDays == null) ||
                            (card.minDays != null &&
                              card.maxDays != null &&
                              card.minDays > card.maxDays)
                          }
                          title="完了"
                          className="flex items-center justify-center rounded p-1 hover:opacity-80 disabled:opacity-40"
                          style={{ background: cardHex, color: "#fff" }}
                        >
                          <CheckIcon style={{ fontSize: 15 }} />
                        </button>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={card.id} className="relative flex-shrink-0">
                    <ScoreCard
                      keyName={key}
                      label={card.label}
                      color={cardHex}
                      count={cards[key] ?? 0}
                    />
                    {isEditMode && (
                      <div className="absolute -top-2 -right-2 flex gap-1">
                        <button
                          onClick={() => openCardEdit(idx)}
                          title="カードを編集"
                          className="flex items-center justify-center rounded-full shadow w-6 h-6 hover:opacity-80"
                          style={{ background: accent, color: "#fff" }}
                        >
                          <EditOutlinedIcon style={{ fontSize: 13 }} />
                        </button>
                        <button
                          onClick={() => removeDeadlineCard(idx)}
                          title="カードを削除"
                          className="flex items-center justify-center rounded-full shadow w-6 h-6 hover:opacity-80"
                          style={{ background: "#EF4444", color: "#fff" }}
                        >
                          <DeleteOutlineIcon style={{ fontSize: 13 }} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              {isEditMode && (
                <button
                  data-tut="dash-add-deadline-card"
                  onClick={addDeadlineCard}
                  className="flex-shrink-0 flex flex-col items-center justify-center rounded-xl px-4 py-3 transition-all min-w-[100px] hover:opacity-80"
                  style={{ border: `2px dashed #F97316`, color: "#F97316" }}
                >
                  <AddIcon style={{ fontSize: 20 }} />
                  <span className="text-xs mt-0.5">カードを追加</span>
                </button>
              )}
            </>
          ) : (
            genericCards().map((c) => (
              <ScoreCard
                key={c.key}
                keyName={c.key}
                label={c.label}
                color={c.color}
                count={cards[c.key] ?? 0}
              />
            ))
          )}
        </div>

        {/* 総タスク数（スコアカードの右下） */}
        <div
          className="text-xs text-right pr-1 mt-1"
          style={{ color: textSecondary }}
        >
          全：{SAMPLE_TASKS.length}タスク
        </div>
      </div>

      {/* ========== テーブル ========== */}
      <div className="flex-1 overflow-auto" data-tut="dash-table">
        <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: isDark ? "#1a1a1a" : "#f9fafb" }}>
              <th
                className="px-3 py-2 text-xs font-semibold text-left whitespace-nowrap"
                style={{ color: textSecondary, minWidth: 220 }}
              >
                タスクタイトル
              </th>
              {visibleColumnKeys.map((k) => {
                const col = activeColumns.find((c) => c.key === k);
                return (
                  <th
                    key={k}
                    className="px-3 py-2 text-xs font-semibold text-left whitespace-nowrap"
                    style={{ color: textSecondary }}
                  >
                    {col?.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {SAMPLE_TASKS.map((t) => (
              <tr
                key={t.id}
                style={{ borderTop: `1px solid ${border}`, background: cardBg }}
              >
                <td className="px-3 py-2" style={{ color: textPrimary }}>
                  {t.title}
                </td>
                {visibleColumnKeys.map((k) => (
                  <td key={k} className="px-3 py-2 whitespace-nowrap">
                    {k === "status" && (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs"
                        style={{
                          background: (STATUS_COLOR[t.status] ?? accent) + "22",
                          color: STATUS_COLOR[t.status] ?? accent,
                        }}
                      >
                        {t.status}
                      </span>
                    )}
                    {k === "assignee" && (
                      <span style={{ color: textPrimary }}>{t.assignee}</span>
                    )}
                    {k === "endDate" && (
                      <span
                        style={{
                          color:
                            t.daysUntil !== null &&
                            t.daysUntil < 0 &&
                            t.status !== "完了"
                              ? "#EF4444"
                              : textSecondary,
                        }}
                      >
                        {endDateLabel(t.daysUntil)}
                      </span>
                    )}
                    {k === "priority" && (
                      <span
                        className="inline-flex items-center gap-1"
                        style={{ color: textPrimary }}
                      >
                        <span
                          className="inline-block w-2 h-2 rounded-full"
                          style={{ background: PRIORITY_COLOR[t.priority] }}
                        />
                        {t.priority}
                      </span>
                    )}
                    {k === "tags" && (
                      <span className="flex gap-1 flex-wrap">
                        {t.tags.map((tag) => (
                          <span
                            key={tag.name}
                            className="px-1.5 py-0.5 rounded text-xs"
                            style={{ background: tag.color + "22", color: tag.color }}
                          >
                            {tag.name}
                          </span>
                        ))}
                      </span>
                    )}
                    {k.startsWith("custom_") && (
                      <span style={{ color: textPrimary }}>
                        {t.custom?.[k] ?? "—"}
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ========== 列設定バー ========== */}
      <div
        className="flex-shrink-0 flex items-center gap-1 px-4 py-2 text-xs"
        style={{ borderTop: `1px solid ${border}`, color: textSecondary }}
      >
        <span>列設定:</span>
        <div data-tut="dash-column-mode" className="flex items-center gap-1">
          <button
            onClick={() => setColumnMode("common")}
            className="flex items-center gap-0.5 px-2 py-0.5 rounded transition-colors"
            style={
              columnMode === "common"
                ? { color: accent, background: accent + "18" }
                : {}
            }
          >
            <PeopleOutlineIcon style={{ fontSize: 13 }} />
            共通
          </button>
          <button
            onClick={() => setColumnMode("personal")}
            className="flex items-center gap-0.5 px-2 py-0.5 rounded transition-colors"
            style={
              columnMode === "personal"
                ? { color: accent, background: accent + "18" }
                : {}
            }
          >
            <PersonOutlineIcon style={{ fontSize: 13 }} />
            個人
          </button>
        </div>
        <button
          data-tut="dash-column-settings-btn"
          onClick={() => {
            setShowColumnDialog(true);
            onAction("dash-open-column-settings");
          }}
          className="flex items-center gap-1 px-2 py-0.5 rounded border transition-colors hover:opacity-80"
          style={{ borderColor: border, color: textSecondary }}
        >
          列の表示設定
        </button>
      </div>
        </div>
      </div>

      {/* ========== 列表示設定ダイアログ ========== */}
      {showColumnDialog && (
        <ColumnSettingsDialog
          accent={accent}
          isDark={isDark}
          initialCommon={savedCommonCols}
          initialPersonal={savedPersonalCols}
          simulateRef={columnDialogSimulateRef}
          onSwitchPersonal={() => onAction("dash-column-personal")}
          onClose={() => setShowColumnDialog(false)}
          onSave={(common, personal) => {
            setSavedCommonCols(common);
            setSavedPersonalCols(personal);
            setShowColumnDialog(false);
            onAction("dash-column-save");
          }}
        />
      )}
    </div>
  );
};

// ===== 列の表示設定ダイアログ（DashboardColumnSettingsDialog を簡略再現） =====

interface DialogProps {
  accent: string;
  isDark: boolean;
  initialCommon: ColumnItem[];
  initialPersonal: ColumnItem[];
  /** スキップ（操作の代行）用。タブ切替・保存を代行する関数を登録する */
  simulateRef?: React.MutableRefObject<
    ((action: TutorialAction) => void) | null
  >;
  onSwitchPersonal: () => void;
  onClose: () => void;
  onSave: (common: ColumnItem[], personal: ColumnItem[]) => void;
}

const ColumnSettingsDialog = ({
  accent,
  isDark,
  initialCommon,
  initialPersonal,
  simulateRef,
  onSwitchPersonal,
  onClose,
  onSave,
}: DialogProps) => {
  const cardBg = isDark ? "#1e1e1e" : "#ffffff";
  const border = isDark ? "#333" : "#e5e7eb";
  const textPrimary = isDark ? "#e0e0e0" : "#212121";
  const textSecondary = isDark ? "#9e9e9e" : "#757575";

  const [activeTab, setActiveTab] = useState<Mode>("common");
  const [common, setCommon] = useState<ColumnItem[]>(
    initialCommon.map((c) => ({ ...c })),
  );
  const [personal, setPersonal] = useState<ColumnItem[]>(
    initialPersonal.map((c) => ({ ...c })),
  );

  const items = activeTab === "common" ? common : personal;
  const setItems = activeTab === "common" ? setCommon : setPersonal;

  const moveUp = (i: number) => {
    if (i === 0) return;
    setItems((prev) => {
      const next = [...prev];
      [next[i - 1], next[i]] = [next[i], next[i - 1]];
      return next;
    });
  };
  const moveDown = (i: number) => {
    setItems((prev) => {
      if (i >= prev.length - 1) return prev;
      const next = [...prev];
      [next[i], next[i + 1]] = [next[i + 1], next[i]];
      return next;
    });
  };
  const toggle = (i: number) =>
    setItems((prev) =>
      prev.map((it, idx) =>
        idx === i ? { ...it, visible: !it.visible } : it,
      ),
    );

  // スキップ（操作の代行）: タブ切替・保存を実際の操作と同じ経路で実行する。
  // 最新の state を閉じ込めるため、毎レンダーで登録し直す。
  useEffect(() => {
    if (!simulateRef) return;
    simulateRef.current = (action: TutorialAction) => {
      if (action === "dash-column-personal") {
        setActiveTab("personal");
        onSwitchPersonal();
      } else if (action === "dash-column-save") {
        onSave(common, personal);
      }
    };
    return () => {
      simulateRef.current = null;
    };
  });

  const tabStyle = (tab: Mode): React.CSSProperties =>
    activeTab === tab
      ? { color: accent, borderBottom: `2px solid ${accent}` }
      : { color: textSecondary };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        data-tut="dash-column-dialog"
        className="relative rounded-lg shadow-xl w-full max-w-sm mx-4 flex flex-col overflow-hidden"
        style={{
          background: cardBg,
          color: textPrimary,
          border: isDark ? "1px solid #000" : "1px solid #fff",
          maxHeight: "80vh",
        }}
      >
        {/* ヘッダー */}
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{
            background: `linear-gradient(135deg, ${accent} 0%, ${accent}cc 100%)`,
          }}
        >
          <span className="font-semibold text-white text-sm">列の表示設定</span>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        {/* タブ */}
        <div className="flex border-b" style={{ borderColor: border }}>
          <button
            onClick={() => setActiveTab("common")}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium"
            style={tabStyle("common")}
          >
            <PeopleOutlineIcon style={{ fontSize: 15 }} />
            共通設定
          </button>
          <button
            data-tut="dash-column-personal-tab"
            onClick={() => {
              setActiveTab("personal");
              onSwitchPersonal();
            }}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium"
            style={tabStyle("personal")}
          >
            <PersonOutlineIcon style={{ fontSize: 15 }} />
            個人設定
          </button>
        </div>

        {/* 本文 */}
        <div className="flex-1 overflow-y-auto p-4">
          <p className="text-xs mb-3" style={{ color: textSecondary }}>
            {activeTab === "common"
              ? "全メンバーが共有する列の表示・順番を設定します。"
              : "自分だけに適用される列の表示・順番を設定します。"}
          </p>

          {/* タイトル（固定） */}
          <div
            className="flex items-center justify-between px-3 py-2 rounded-lg mb-1"
            style={{
              background: isDark ? "#2a2a2a" : "#f9fafb",
              border: `1px solid ${border}`,
            }}
          >
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked
                disabled
                className="opacity-40 cursor-not-allowed"
              />
              <span className="text-sm">タスクタイトル</span>
            </div>
            <span className="text-xs" style={{ color: textSecondary }}>
              常に先頭・固定
            </span>
          </div>

          {/* 設定可能な列 */}
          {items.map((item, index) => (
            <div
              key={item.key}
              data-tut={item.isCustom ? "dash-column-custom" : undefined}
              className="flex items-center justify-between px-3 py-2 rounded-lg mb-1"
              style={{ border: `1px solid ${border}` }}
            >
              <label className="flex items-center gap-2 cursor-pointer flex-1">
                <input
                  type="checkbox"
                  checked={item.visible}
                  onChange={() => toggle(index)}
                  className="cursor-pointer"
                />
                <span
                  className="text-sm"
                  style={{ color: item.visible ? undefined : textSecondary }}
                >
                  {item.label}
                </span>
                {item.isCustom && (
                  <span
                    className="text-xs px-1 rounded"
                    style={{ background: accent + "20", color: accent }}
                  >
                    カスタム
                  </span>
                )}
              </label>
              <div className="flex gap-0.5">
                <button
                  onClick={() => moveUp(index)}
                  disabled={index === 0}
                  className="p-1 rounded hover:bg-black/5 disabled:opacity-20"
                  style={{ color: textSecondary }}
                >
                  <ArrowUpwardIcon style={{ fontSize: 14 }} />
                </button>
                <button
                  onClick={() => moveDown(index)}
                  disabled={index === items.length - 1}
                  className="p-1 rounded hover:bg-black/5 disabled:opacity-20"
                  style={{ color: textSecondary }}
                >
                  <ArrowDownwardIcon style={{ fontSize: 14 }} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* フッター */}
        <div
          className="flex items-center justify-end gap-2 px-4 py-3 border-t"
          style={{ borderColor: border }}
        >
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded border hover:bg-black/5"
            style={{ borderColor: border }}
          >
            キャンセル
          </button>
          <button
            data-tut="dash-column-save"
            onClick={() => onSave(common, personal)}
            className="px-4 py-1.5 text-sm rounded text-white"
            style={{ backgroundColor: accent }}
          >
            保存する
          </button>
        </div>
      </div>
    </div>
  );
};